import axios from "axios";
import config from "@Config/index";

interface University {
  label: string;
  value: string;
  inventoryCount: number;
}

interface RegionData {
  name: string;
  name_zh?: string;
  canonical_name: string;
}

interface InventoryCount {
  [key: string]: number;
}

const fetchUniversityData = async ({
  ancestorId = "",
  regionType = "establishment",
  searchTerm = "",
  sortKey = "name",
  isWhitelabel = false,
  partner_id = "",
  lang,
}: {
  ancestorId?: string;
  regionType?: string;
  searchTerm?: string;
  sortKey?: string;
  isWhitelabel?: boolean;
  partner_id?: string;
  lang?: string;
}): Promise<University[]> => {
  try {
    const regionResponse = await axios.get<{ data: { result: RegionData[] } }>(
      `${config.API_URL}/api/v0/regions`,
      {
        params: {
          region_type: regionType,
          sort_key: sortKey,
          sort_order: "asc",
          only: "name,canonical_name",
          ancestor_id: ancestorId,
          state: "active",
          limit: 500,
          ...(!isWhitelabel && { exclude_whitelabel_regions: true }),
          ...(isWhitelabel && { whitelabel_partner_ids: partner_id }),
          ...(searchTerm.length > 0 && { search_name: searchTerm }),
          ...(config.IS_CHINA_APP && lang && { lang }),
          use_java_api: true,
        },
      },
    );

    // Transform university data
    const universities = regionResponse.data.data.result.map((region) => ({
      label: region.name_zh || region.name,
      value: region.canonical_name,
      inventoryCount: 0, // Placeholder to be updated
    }));
    return universities;

    // Skip inventory fetch if no universities
    if (universities.length === 0) return universities;

    // Fetch inventory counts
    const regionNames = universities.map((uni) => uni.value).join(",");
    const inventoryResponse = await axios.get<{ data: InventoryCount }>(
      `${config.API_URL}/api/v1/inventories/region_inventory_count`,
      {
        params: { region_canonical_names: regionNames },
      },
    );

    // Update inventory counts
    return universities.map((university) => ({
      ...university,
      inventoryCount: inventoryResponse.data.data[university.value] || 0,
    }));
  } catch (error) {
    console.error("Error in university data fetch:", error);
    throw new Error("Failed to fetch university data");
  }
};

export default fetchUniversityData;
